import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchWebsiteConfig } from "../api/websiteContentApi";
import {
  normalizeContentNavigationConfig,
  normalizeHostedFormsConfig,
  normalizeNavigationConfig,
  normalizePublicBookingBenefitsConfig,
} from "../utils/websiteConfig";
import { normalizePromotionRolloutConfig } from "../utils/websitePromotionRollout";
import { normalizeSpotlightRolloutConfig } from "../utils/websiteSpotlightRollout";
import { normalizeSupportRolloutConfig } from "../utils/supportRollout";
import {
  normalizeImageWorkRolloutConfig,
  setRuntimeImageWorkRolloutConfig,
} from "../utils/imageWorkRollout";
import {
  normalizePortalImageRolloutConfig,
  setRuntimePortalImageRolloutConfig,
} from "../utils/portalImageRollout";

import { useHomepageBootstrap } from "./HomepageRenderContext";

const normalizeConfig = (data) => ({
  status: "ready",
  navigation: normalizeNavigationConfig(data?.navigation),
  contentNavigation: normalizeContentNavigationConfig(data?.contentNavigation),
  footer: data?.footer || {},
  hostedForms: normalizeHostedFormsConfig(data?.hostedForms),
  publicBookingBenefits: normalizePublicBookingBenefitsConfig(data?.publicBookingBenefits),
  promotionsRollout: normalizePromotionRolloutConfig(data?.promotionsRollout),
  spotlightsRollout: normalizeSpotlightRolloutConfig(data?.spotlightsRollout),
  supportRollout: normalizeSupportRolloutConfig(data?.supportRollout),
  imageWorkRollout: normalizeImageWorkRolloutConfig(data?.imageWorkRollout),
  portalImageRollout: normalizePortalImageRolloutConfig(data?.portalImageRollout),
});
const WebsiteConfigContext = createContext(null);

export function WebsiteConfigProvider({ children, enabled = true }) {
  const initialBootstrap = useHomepageBootstrap();
  const [state, setState] = useState(() => enabled && initialBootstrap?.config
    ? normalizeConfig(initialBootstrap.config) : ({
    status: "idle",
    footer: {},
    navigation: {},
    contentNavigation: { items: [], primaryLinks: [], footerLinks: [] },
    hostedForms: [],
    publicBookingBenefits: normalizePublicBookingBenefitsConfig(),
    promotionsRollout: normalizePromotionRolloutConfig(),
    spotlightsRollout: normalizeSpotlightRolloutConfig(),
    supportRollout: normalizeSupportRolloutConfig(),
    imageWorkRollout: normalizeImageWorkRolloutConfig(),
    portalImageRollout: normalizePortalImageRolloutConfig(),
  }));

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let intervalId = null;
    setState((prev) => prev.status === "ready" ? prev : ({ ...prev, status: "loading" }));
    const loadConfig = () => fetchWebsiteConfig()
      .then((data) => {
        if (!active) return;
        const next = normalizeConfig(data);
        setRuntimeImageWorkRolloutConfig(next.imageWorkRollout);
        setRuntimePortalImageRolloutConfig(next.portalImageRollout);
        setState(next);
      })
      .catch(() => {
        if (!active) return;
        setState((prev) => ({ ...prev, status: prev.status === "ready" ? "ready" : "error" }));
      });
    const bootstrap = initialBootstrap;
    if (bootstrap?.config) {
      const initial = normalizeConfig(bootstrap.config);
      setRuntimeImageWorkRolloutConfig(initial.imageWorkRollout);
      setRuntimePortalImageRolloutConfig(initial.portalImageRollout);
    } else loadConfig();
    intervalId = window.setInterval(loadConfig, 60_000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") loadConfig();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      active = false;
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled]);

  const value = useMemo(() => state, [state]);

  return <WebsiteConfigContext.Provider value={value}>{children}</WebsiteConfigContext.Provider>;
}

export function useWebsiteConfig({ optional = false } = {}) {
  const context = useContext(WebsiteConfigContext);
  if (!context && !optional) {
    throw new Error("useWebsiteConfig must be used within a WebsiteConfigProvider");
  }
  return context;
}
